import {
  isFoundationSlab,
  isWallExteriorFinishBRep,
  isBuildingAnalyticExtrusion,
  isBuildingAnalyticEntityHandle,
} from "building-common/analyticEntity/buildingAnalyticEntitySelectors";
import type { BuildingAnalyticExtrusion, BuildingAnalyticEntity } from "building-types/buildingAnalyticEntityTypes";
import { isBuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle, RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { GeneratedBuildingEntityHandle } from "building-types/handles/generatedEntityHandleTypes";
import type { Table } from "common/data/table";
import { notNode } from "common/graph/entityGraph";
import type { EntityRelationship } from "common/graph/entityGraphTypes";
import { pathStringFromMaterialSrcWithUserDefinedName } from "common/materials/pathStringFromMaterialSrc";
import {
  docEntityHandleIsGeneratedBuildingEntityHandle,
  docEntityHandleIsGeneratedEntity,
} from "doc/entity/docEntityHandleTools";
import type { RelationshipType } from "doc/utils/buildingEntityIndex";
import { searchDomainBelow } from "doc/utils/buildingEntityIndex";
import type { BuildingGenerationContextInterface } from "doc/utils/buildingGenerationContext";
import type { AABB3 } from "geom/aabb3Types";
import { faceAABB3, type Face } from "geom/brep";
import { polygonIntersection } from "geom/polyclip/polyClip";
import { polygonArea } from "geom/polyLine2Polygon";

export type StructureBelow = "CONCRETE SLAB" | "FRAMED FLOOR";
export type RelationshipPredicate = (relationship: EntityRelationship<string, RelationshipType>) => boolean;

export function host(
  relationship: EntityRelationship<string, RelationshipType>,
): relationship is EntityRelationship<string, "HOSTS"> {
  return relationship.type === "HOSTS";
}

export function adjacent(
  relationship: EntityRelationship<string, RelationshipType>,
): relationship is EntityRelationship<string, "ADJACENT_TO"> {
  return relationship.type === "ADJACENT_TO";
}

export function isRoomHandle(handle?: DocEntityHandle): handle is RoomHandle {
  return !!handle && handle.type === "ROOM";
}

export function slabsBelow(
  ctx: BuildingGenerationContextInterface,
  handle: DocEntityHandle,
  analyticEntities: Table<BuildingAnalyticEntity>,
  searchDistance: number = 12,
  buffer: number = 0.1,
): BuildingAnalyticExtrusion[] {
  const handleAABB = ctx.indexNodeAABB(handle);
  if (!handleAABB) {
    return [];
  }

  return foundationSlabsBelowAABB(ctx, handleAABB, analyticEntities, searchDistance, buffer);
}

export function slabsBelowFace(
  ctx: BuildingGenerationContextInterface,
  face: Face,
  analyticEntities: Table<BuildingAnalyticEntity>,
  searchDistance: number = 12,
  buffer: number = 0.1,
): BuildingAnalyticExtrusion[] {
  return foundationSlabsBelowAABB(ctx, faceAABB3(face), analyticEntities, searchDistance, buffer).filter((slab) => {
    const overlap = polygonIntersection({ pls: [face.loops, slab.profileInPlan.loops] });
    return overlap.some((polygon) => polygonArea(polygon) > 0);
  });
}

function foundationSlabsBelowAABB(
  ctx: BuildingGenerationContextInterface,
  aabb: AABB3,
  analyticEntities: Table<BuildingAnalyticEntity>,
  searchDistance: number,
  buffer: number,
): BuildingAnalyticExtrusion[] {
  const slabs: BuildingAnalyticExtrusion[] = [];
  const below = searchDomainBelow(aabb, searchDistance, buffer);
  for (const belowHandle of ctx.indexSpatialSearch(below)) {
    if (!docEntityHandleIsGeneratedEntity(belowHandle)) {
      continue;
    }
    const handleEntity = belowHandle.entity;
    if (!isBuildingEntityHandle(handleEntity)) {
      continue;
    }
    if (!isBuildingAnalyticEntityHandle(handleEntity)) {
      continue;
    }
    const analyticEntity = analyticEntities[handleEntity.id];
    if (!analyticEntity) {
      continue;
    }
    if (!isBuildingAnalyticExtrusion(analyticEntity)) {
      continue;
    }
    if (!isFoundationSlab(analyticEntity)) {
      continue;
    }
    slabs.push(analyticEntity);
  }
  return slabs;
}

export function structureBelowAdjacentRooms(
  ctx: BuildingGenerationContextInterface,
  handle: DocEntityHandle | undefined,
  analyticEntities: Table<BuildingAnalyticEntity>,
  searchDistance: number = 12,
): StructureBelow {
  let structure: StructureBelow = "FRAMED FLOOR";

  if (!handle) {
    return structure;
  }

  const rooms = adjacentRooms(ctx, handle);

  if (rooms.length > 0) {
    for (const room of Array.from(rooms)) {
      if (slabsBelow(ctx, room, analyticEntities, searchDistance).length > 0) {
        structure = "CONCRETE SLAB";
        break;
      }
    }
  }

  return structure;
}

export function adjacentRooms(
  ctx: BuildingGenerationContextInterface,
  handle: DocEntityHandle | undefined,
): RoomHandle[] {
  if (!handle) {
    return [];
  }
  return relatedEntities(ctx, handle, adjacent, isRoomHandle);
}

export function relatedEntities<T extends DocEntityHandle>(
  ctx: BuildingGenerationContextInterface,
  handle: DocEntityHandle,
  relationshipPredicate: RelationshipPredicate,
  predicate: (handle: DocEntityHandle) => handle is T = (_handle): _handle is T => true,
): T[] {
  const results = new Set<T>();
  const notRoot = notNode(handle);
  ctx
    .indexRelationships(handle)
    .filter(relationshipPredicate)
    .forEach((r) => {
      const nodeA = ctx.indexNodeByHash(r.b);
      if (nodeA && notRoot(nodeA) && predicate(nodeA)) {
        results.add(nodeA);
      }
      const nodeB = ctx.indexNodeByHash(r.a);
      if (nodeB && notRoot(nodeB) && predicate(nodeB)) {
        results.add(nodeB);
      }
    });
  return Array.from(results);
}

/**
 * retrieves adjacent analytic entities tagged as exterior wall finishes and
 * gathers the unique materials
 */
export function adjacentExteriorFinishMaterials(
  ctx: BuildingGenerationContextInterface,
  analyticEntities: Table<BuildingAnalyticEntity>,
  relationships: EntityRelationship<string, RelationshipType>[],
) {
  const materials = new Set<string>();
  for (const r of relationships) {
    if (!adjacent(r)) {
      continue;
    }

    // nodes can have many types; we only want to process generated entities
    const node = ctx.indexNodeByHash(r.b);
    if (!docEntityHandleIsGeneratedEntity(node)) {
      continue;
    }

    // some generated entities are not building entities; we only want to process building entities
    const entity = node.entity;
    if (!isBuildingEntityHandle(entity)) {
      continue;
    }

    // ensure the entity is a valid analytic entity and tagged as a wall exterior finish
    const analyticEntity = analyticEntities[entity.id];
    if (!isWallExteriorFinishBRep(analyticEntity)) {
      continue;
    }

    if (analyticEntity.materialSrc) {
      materials.add(pathStringFromMaterialSrcWithUserDefinedName(analyticEntity.materialSrc));
    }
  }
  return Array.from(materials).sort();
}

export function searchEntityDomain(
  ctx: BuildingGenerationContextInterface,
  handle?: DocEntityHandle,
  domainModifier: (aabb: AABB3) => AABB3 = (aabb) => aabb,
): GeneratedBuildingEntityHandle[] {
  if (!handle) {
    return [];
  }

  const handleAABB = ctx.indexNodeAABB(handle);
  if (!handleAABB) {
    return [];
  }

  return ctx.indexSpatialSearch(domainModifier(handleAABB)).filter(docEntityHandleIsGeneratedBuildingEntityHandle);
}
